// Screen query/mutation hooks (screens.rs). GET/POST /v1/locations/{locationId}/screens,
// GET /v1/screens/{id} (server endpoint lands in Task 4 — the hook is wired
// now so screen-detail pages have it from day one), PATCH/DELETE
// /v1/screens/{id}, POST /v1/screens/{id}/token/rotate,
// DELETE /v1/screens/{id}/template.
//
// All five mutations invalidate both a location's screens list and the screen
// detail cache: displayToken/displayPath and templateId are both fields the
// list rows themselves render (token rotation and template unlink change
// exactly those), not just the detail view.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "./http";
import { screenKeys, searchKeys, statusKeys } from "./keys";
import type { CreateScreen, PatchScreen, RotatedToken, Screen } from "./types";

export function useScreens(locationId: string) {
    return useQuery({
        queryKey: screenKeys.list(locationId),
        queryFn: () => http.get<Screen[]>(`/v1/locations/${locationId}/screens`),
        enabled: Boolean(locationId),
    });
}

export function useScreen(screenId: string) {
    return useQuery({
        queryKey: screenKeys.detail(screenId),
        queryFn: () => http.get<Screen>(`/v1/screens/${screenId}`),
        enabled: Boolean(screenId),
    });
}

export function useCreateScreen(locationId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateScreen) => http.post<Screen>(`/v1/locations/${locationId}/screens`, { json: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: screenKeys.list(locationId) });
        },
    });
}

export function usePatchScreen(locationId: string, screenId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: PatchScreen) => http.patch<Screen>(`/v1/screens/${screenId}`, { json: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: screenKeys.list(locationId) });
            queryClient.invalidateQueries({ queryKey: screenKeys.detail(screenId) });
        },
    });
}

export function useDeleteScreen(workspaceId: string, locationId: string, screenId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => http.delete<void>(`/v1/screens/${screenId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: screenKeys.list(locationId) });
            // The workspace-wide surfaces list screens too, and their 30s
            // staleTime would otherwise show the deleted row on the way back.
            queryClient.invalidateQueries({ queryKey: searchKeys.screensAll(workspaceId) });
            queryClient.invalidateQueries({ queryKey: statusKeys.list(workspaceId) });
            // Removed rather than invalidated — the screen is gone, so a
            // refetch of the detail query would just 404.
            queryClient.removeQueries({ queryKey: screenKeys.detail(screenId) });
        },
    });
}

export function useRotateToken(locationId: string, screenId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => http.post<RotatedToken>(`/v1/screens/${screenId}/token/rotate`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: screenKeys.list(locationId) });
            queryClient.invalidateQueries({ queryKey: screenKeys.detail(screenId) });
        },
    });
}
